// Appels fetch des pages publiques à jeton, sans authentification.
// Une erreur porte le statut HTTP et le message serveur.

import * as E from "fp-ts/Either"
import * as J from "fp-ts/Json"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"

/** Origine de l'API, vide en production (même origine). */
export const PUBLIC_API_BASE = pipe(
  O.fromNullable(import.meta.env.VITE_API_URL),
  O.getOrElse(() => ""),
)

/** Erreur d'un appel public : statut HTTP, message, code serveur. */
type PublicError = Error & {
  status: number
  /** `detail` de la réponse Problem Details du serveur. */
  detail: O.Option<string>
  /** `code` machine du serveur (`TENANT_CLOSED`…). Racine ou sous `error`. */
  code: O.Option<string>
}

/** Objet JSON d'une réponse, champs non typés. */
type ReplyRecord = Readonly<Record<string, unknown>>

// Réponse d'erreur illisible : panne entre la page et l'API.
const UNREADABLE_REPLY: ReplyRecord = { error: { message: "Erreur réseau" } }

function isReplyRecord(value: unknown): value is ReplyRecord {
  return typeof value === "object" && value instanceof Object && !Array.isArray(value)
}

/** Champ texte non vide d'un objet de réponse. */
function textField(reply: ReplyRecord, key: string): O.Option<string> {
  return pipe(
    O.fromNullable(reply[key]),
    O.filter((value): value is string => typeof value === "string"),
    O.filter((text) => text.trim() !== ""),
  )
}

/** Corps d'une réponse d'erreur, de forme inconnue. */
function errorFields(replyText: string): ReplyRecord {
  return pipe(
    J.parse(replyText),
    E.fold(
      (): ReplyRecord => UNREADABLE_REPLY,
      (parsed): ReplyRecord => (isReplyRecord(parsed) ? parsed : {}),
    ),
  )
}

function toError(status: number, problem: ReplyRecord, fallback: string): PublicError {
  const nested = pipe(
    O.fromNullable(problem.error),
    O.filter(isReplyRecord),
    O.getOrElse((): ReplyRecord => ({})),
  )
  // Problem Details : le message du serveur est dans `detail`.
  const detail = textField(problem, "detail")
  const message = pipe(
    textField(nested, "message"),
    O.alt(() => textField(problem, "message")),
    O.alt(() => detail),
    O.getOrElse(() => fallback),
  )
  const code = pipe(
    textField(problem, "code"),
    O.alt(() => textField(nested, "code")),
  )
  return Object.assign(new Error(message), { status, detail, code })
}

/**
 * Message du serveur porté par une erreur d'un appel public.
 *
 * @example
 * pipe(serverDetail(query.error), O.getOrElse(() => "Lien invalide."))
 */
export function serverDetail(error: unknown): O.Option<string> {
  return isPublicError(error) ? error.detail : O.none
}

/**
 * Code machine du serveur, porté par l'erreur d'un appel public.
 *
 * @example
 * pipe(serverCode(query.error), O.exists((code) => code === "TENANT_CLOSED"))
 */
export function serverCode(error: unknown): O.Option<string> {
  return isPublicError(error) ? error.code : O.none
}

// Seul toError pose `detail` sur une erreur.
function isPublicError(error: unknown): error is PublicError {
  return error instanceof Error && "detail" in error
}

/**
 * Corps JSON d'une réponse réussie, sinon l'erreur du serveur.
 * @template T Corps attendu de la réponse.
 */
function readReply<T>(response: Response, fallback: string): Promise<T> {
  if (response.ok) return response.json()
  return response.text().then((replyText) => {
    throw toError(response.status, errorFields(replyText), fallback)
  })
}

export function publicGet<T>(path: string): Promise<T> {
  return fetch(`${PUBLIC_API_BASE}${path}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  }).then((response) => readReply<T>(response, "Erreur de chargement"))
}

// Le chemin varie selon l'appelant : l'en-tête CSRF part toujours.
export function publicPost<T>(path: string, requestJson: unknown): Promise<T> {
  return fetch(`${PUBLIC_API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    body: JSON.stringify(requestJson),
  }).then((response) => readReply<T>(response, "Erreur de soumission"))
}
